/**
 * LevelManagerModal (S2.3) — seniority-band CRUD + reorder (fleet
 * GroupManagementModal parity, level flavour).
 *
 * Bands are DISPLAY grouping only (sort/colour) — never an inheritance driver
 * (plan §0 axis non-conflation), so nothing in this modal can change a grant.
 * Reorder is the sanctioned NO-DEP up/down control (dnd-kit is deliberately not
 * a dependency). House tenet hide-don't-disable for capability gates: the
 * system band shows a lock tag + tooltip INSTEAD of its controls; an in-use
 * band shows its usage count instead of a delete affordance. Reorder
 * BOUNDARIES are the sanctioned workflow-state DISABLE instead (ux-review):
 * both arrows stay rendered and the impossible direction disables, so the
 * ladder is position-stable — repeated "move up" clicks never make the down
 * arrow slide under the cursor (the misclick trap hiding would create). Every
 * icon-only control carries a full-word tooltip AND `aria-label` — never a
 * bare glyph.
 */

import { useState } from 'react';
import { App, Button, Input, Modal, Space, Tag, Tooltip, Typography, theme } from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { controlHeightTokens } from '@/styles/tokens';
import { useRoleHierarchyMutations, useRoleLevels } from './useRoleHierarchy';
import type { GovernanceRole, RoleLevel } from './model';

const { Text } = Typography;

export interface LevelManagerModalProps {
  open: boolean;
  /** The merged role catalog — used for the per-band usage census. */
  roles: GovernanceRole[];
  onCancel: () => void;
  /** Fired after any level mutation so the panel re-reads its grouping. */
  onChanged: () => void;
}

/** Map a mock 409/422 error code to its localized message key. */
function errorKey(error: unknown): string {
  const code =
    error instanceof AxiosError ? (error.response?.data as { error?: string })?.error : undefined;
  if (code === 'SYSTEM_LEVEL_LOCKED') return 'roleLevels.systemLockedTooltip';
  if (code === 'LEVEL_IN_USE') return 'roleLevels.deleteBlockedInUse';
  return 'messages.loadFailed';
}

const LevelManagerModal = ({ open, roles, onCancel, onChanged }: LevelManagerModalProps) => {
  const { t } = useTranslation('governance');
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const { levels, loading, refresh } = useRoleLevels();
  const { createLevel, renameLevel, reorderLevel, deleteLevel } = useRoleHierarchyMutations();

  const [newName, setNewName] = useState('');
  const [renameTarget, setRenameTarget] = useState<RoleLevel | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const sorted = [...(levels ?? [])].sort((a, b) => a.order - b.order);
  const usageByLevel = new Map<string, number>();
  roles.forEach((role) => {
    if (role.levelId) usageByLevel.set(role.levelId, (usageByLevel.get(role.levelId) ?? 0) + 1);
  });

  const run = async (action: () => Promise<unknown>, successKey: string) => {
    setBusy(true);
    try {
      await action();
      message.success(t(successKey));
      refresh();
      onChanged();
    } catch (error) {
      message.error(t(errorKey(error)));
    } finally {
      setBusy(false);
    }
  };

  const submitAdd = () => {
    const name = newName.trim();
    if (!name) {
      message.error(t('roleLevels.nameRequired'));
      return;
    }
    void run(async () => {
      await createLevel(name);
      setNewName('');
    }, 'roleLevels.created');
  };

  const submitRename = () => {
    if (!renameTarget) return;
    const name = renameDraft.trim();
    if (!name) {
      message.error(t('roleLevels.nameRequired'));
      return;
    }
    void run(async () => {
      await renameLevel(renameTarget, name);
      setRenameTarget(null);
    }, 'roleLevels.renamed');
  };

  return (
    <Modal
      open={open}
      title={t('roleLevels.title')}
      footer={null}
      onCancel={onCancel}
      maskClosable={false}
      destroyOnHidden
      width={560}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: token.marginSM }}>
        {t('roleLevels.hint')}
      </Text>

      <div style={{ display: 'flex', flexDirection: 'column', gap: token.marginXS }}>
        {sorted.map((level, index) => {
          const neighbourUp = index > 0 ? sorted[index - 1] : undefined;
          const neighbourDown = index < sorted.length - 1 ? sorted[index + 1] : undefined;
          const usage = usageByLevel.get(level.id) ?? 0;
          const renaming = renameTarget?.id === level.id;
          // Reorder boundary = workflow-state DISABLE (not hide): impossible
          // moves (boundary, or a system band on either side) render the arrow
          // disabled so the ladder's controls never shift mid-interaction.
          const canMoveUp = !level.isSystem && Boolean(neighbourUp) && !neighbourUp?.isSystem;
          const canMoveDown = !level.isSystem && Boolean(neighbourDown) && !neighbourDown?.isSystem;
          return (
            <div
              key={level.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: token.marginXS,
                padding: `${token.paddingXS}px ${token.paddingSM}px`,
                background: token.colorFillQuaternary,
                borderRadius: token.borderRadiusLG,
              }}
            >
              {renaming ? (
                <Space.Compact style={{ flex: 1 }}>
                  <Input
                    size="large"
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onPressEnter={submitRename}
                    autoFocus
                  />
                  <Tooltip title={t('roleLevels.saveName')}>
                    <Button
                      size="large"
                      type="primary"
                      icon={<CheckOutlined />}
                      aria-label={t('roleLevels.saveName')}
                      loading={busy}
                      onClick={submitRename}
                    />
                  </Tooltip>
                  <Tooltip title={t('roleLevels.cancelRename')}>
                    <Button
                      size="large"
                      icon={<CloseOutlined />}
                      aria-label={t('roleLevels.cancelRename')}
                      onClick={() => setRenameTarget(null)}
                    />
                  </Tooltip>
                </Space.Compact>
              ) : (
                <Space size={token.marginXS} wrap>
                  <Text strong>{level.name}</Text>
                  {level.isSystem && (
                    <Tooltip title={t('roleLevels.systemLockedTooltip')} trigger={['hover', 'focus']}>
                      <Tag tabIndex={0} icon={<LockOutlined aria-hidden />} color="default">
                        {t('roleLevels.systemLocked')}
                      </Tag>
                    </Tooltip>
                  )}
                  {usage > 0 && <Tag>{t('roleLevels.roleCount', { total: usage })}</Tag>}
                </Space>
              )}

              {!renaming && !level.isSystem && (
                <Space size={4}>
                  <Tooltip title={t('roleLevels.moveUp')}>
                    <Button
                      type="text"
                      icon={<ArrowUpOutlined />}
                      aria-label={`${t('roleLevels.moveUp')} — ${level.name}`}
                      disabled={busy || !canMoveUp}
                      onClick={() => void run(() => reorderLevel(level, 'up'), 'roleLevels.reordered')}
                    />
                  </Tooltip>
                  <Tooltip title={t('roleLevels.moveDown')}>
                    <Button
                      type="text"
                      icon={<ArrowDownOutlined />}
                      aria-label={`${t('roleLevels.moveDown')} — ${level.name}`}
                      disabled={busy || !canMoveDown}
                      onClick={() =>
                        void run(() => reorderLevel(level, 'down'), 'roleLevels.reordered')
                      }
                    />
                  </Tooltip>
                  <Tooltip title={t('roleLevels.rename')}>
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      aria-label={`${t('roleLevels.rename')} — ${level.name}`}
                      onClick={() => {
                        setRenameTarget(level);
                        setRenameDraft(level.name);
                      }}
                    />
                  </Tooltip>
                  {usage === 0 && (
                    <Tooltip title={t('roleLevels.delete')}>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        aria-label={`${t('roleLevels.delete')} — ${level.name}`}
                        disabled={busy}
                        onClick={() => void run(() => deleteLevel(level), 'roleLevels.deleted')}
                      />
                    </Tooltip>
                  )}
                </Space>
              )}
            </div>
          );
        })}
        {!loading && sorted.length === 0 && (
          <Text type="secondary">{t('hierarchy.empty')}</Text>
        )}
      </div>

      <Space.Compact style={{ width: '100%', marginTop: token.marginSM }}>
        <Input
          size="large"
          value={newName}
          placeholder={t('roleLevels.addPlaceholder')}
          onChange={(e) => setNewName(e.target.value)}
          onPressEnter={submitAdd}
        />
        <Button
          size="large"
          icon={<PlusOutlined />}
          style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
          loading={busy}
          onClick={submitAdd}
        >
          {t('roleLevels.add')}
        </Button>
      </Space.Compact>
    </Modal>
  );
};

export default LevelManagerModal;
